import { useState, useEffect } from 'react';
import { ItemCard } from '../inventory/ItemCard';
import { SkeletonItemCard } from '../ui/Skeleton';
import { useProfileItems } from '../../hooks/useCommunity';
import { useTranslation } from '../../i18n';
import styles from './ProfileItemsGrid.module.css';

interface Props {
  slug: string;
  vaultId?: string;
}

const FILTERS = ['all', 'showcase', 'for-trade', 'for-sale'] as const;

export function ProfileItemsGrid({ slug, vaultId }: Props) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  // Reset page when vault changes
  useEffect(() => { setPage(1); }, [vaultId]);

  const query: Record<string, string> = { filter, page: String(page), limit: '20' };
  if (vaultId) query.vaultId = vaultId;
  const { data, isLoading } = useProfileItems(slug, query);

  const filterLabels: Record<string, string> = {
    all: t('community.filterAll'),
    showcase: t('community.filterShowcase'),
    'for-trade': t('community.filterForTrade'),
    'for-sale': t('community.filterForSale'),
  };

  return (
    <div>
      <div className={styles.filterBar}>
        {FILTERS.map((f) => (
          <button
            key={f}
            className={`${styles.filterTab} ${filter === f ? styles.filterTabActive : ''}`}
            onClick={() => { setFilter(f); setPage(1); }}
          >
            {filterLabels[f]}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className={styles.grid}>
          {Array.from({ length: 8 }).map((_, i) => (
            <SkeletonItemCard key={i} />
          ))}
        </div>
      ) : data && data.data.length > 0 ? (
        <>
          <div className={styles.grid}>
            {data.data.map((item) => (
              <ItemCard key={item.id} item={item} showOwner={false} from="profile" />
            ))}
          </div>
          {data.totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                className={styles.paginationBtn}
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                &larr;
              </button>
              <span className={styles.paginationInfo}>{page} / {data.totalPages}</span>
              <button
                className={styles.paginationBtn}
                disabled={page >= data.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                &rarr;
              </button>
            </div>
          )}
        </>
      ) : (
        <div className={styles.emptyState}>{t('community.noPublicItems')}</div>
      )}
    </div>
  );
}
